/**
 * Simple sessionStorage cache utility with TTL (time-to-live) support.
 * Data expires after `ttlMs` milliseconds (default: 5 minutes).
 * Uses sessionStorage so cache is cleared when the tab/browser closes.
 */

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Store a value in cache with an expiry timestamp.
 * @param {string} key - Cache key
 * @param {*} data - Data to cache (must be JSON-serializable)
 * @param {number} [ttlMs] - Time-to-live in milliseconds
 */
export function setCache(key, data, ttlMs = DEFAULT_TTL) {
  try {
    const entry = {
      data,
      expiresAt: Date.now() + ttlMs,
    };
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch (e) {
    // sessionStorage might be full or unavailable – fail silently
    console.warn('[cache] Could not write to sessionStorage:', e);
  }
}

/**
 * Retrieve a cached value. Returns null if missing or expired.
 * @param {string} key - Cache key
 * @returns {*|null} Cached data or null
 */
export function getCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() > entry.expiresAt) {
      sessionStorage.removeItem(key);
      return null;
    }
    return entry.data;
  } catch (e) {
    return null;
  }
}

/**
 * Invalidate (remove) a cached entry.
 * @param {string} key - Cache key
 */
export function invalidateCache(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    // fail silently
  }
}
